// ProjectContext.tsx - Manage graphic novel project state
import React, { createContext, useContext, useReducer, ReactNode } from 'react';

// Define TypeScript interfaces
interface Drawing {
  id: string;
  [key: string]: any;
}

interface Bubble {
  id: string;
  [key: string]: any;
}

interface Character {
  id: string;
  [key: string]: any;
}

interface Prop {
  id: string;
  [key: string]: any;
}

interface Page {
  id: string;
  drawings: Drawing[];
  bubbles: Bubble[];
  characters: Character[];
  props: Prop[];
  background: string | null;
  backgroundColor?: string;
  [key: string]: any;
}

interface ProjectState {
  projectName: string;
  author: string;
  pages: Page[];
  currentPageIndex: number;
  lastSaved: Date | null;
}

// Action types as enum
enum ActionTypes {
  SET_PROJECT_NAME = 'SET_PROJECT_NAME',
  SET_AUTHOR = 'SET_AUTHOR',
  ADD_PAGE = 'ADD_PAGE',
  DELETE_PAGE = 'DELETE_PAGE',
  REORDER_PAGES = 'REORDER_PAGES',
  SET_CURRENT_PAGE = 'SET_CURRENT_PAGE',
  UPDATE_PAGE = 'UPDATE_PAGE',
  ADD_DRAWING = 'ADD_DRAWING',
  ADD_BUBBLE = 'ADD_BUBBLE',
  UPDATE_BUBBLE = 'UPDATE_BUBBLE',
  DELETE_BUBBLE = 'DELETE_BUBBLE',
  ADD_CHARACTER = 'ADD_CHARACTER',
  UPDATE_CHARACTER = 'UPDATE_CHARACTER',
  DELETE_CHARACTER = 'DELETE_CHARACTER',
  ADD_PROP = 'ADD_PROP',
  UPDATE_PROP = 'UPDATE_PROP',
  DELETE_PROP = 'DELETE_PROP',
  SET_BACKGROUND = 'SET_BACKGROUND',
  SAVE_PROJECT = 'SAVE_PROJECT',
  LOAD_PROJECT = 'LOAD_PROJECT',
  CLEAR_PROJECT = 'CLEAR_PROJECT'
}

// Define action interfaces
interface SetProjectNameAction {
  type: ActionTypes.SET_PROJECT_NAME;
  payload: string;
}

interface SetAuthorAction {
  type: ActionTypes.SET_AUTHOR;
  payload: string;
}

interface AddPageAction {
  type: ActionTypes.ADD_PAGE;
}

interface DeletePageAction {
  type: ActionTypes.DELETE_PAGE;
  payload: number;
}

interface ReorderPagesAction {
  type: ActionTypes.REORDER_PAGES;
  payload: { fromIndex: number; toIndex: number };
}

interface SetCurrentPageAction {
  type: ActionTypes.SET_CURRENT_PAGE;
  payload: number;
}

interface UpdatePageAction {
  type: ActionTypes.UPDATE_PAGE;
  payload: { index: number; data: Partial<Page> };
}

interface AddDrawingAction {
  type: ActionTypes.ADD_DRAWING;
  payload: Drawing;
}

interface AddBubbleAction {
  type: ActionTypes.ADD_BUBBLE;
  payload: Bubble;
}

interface UpdateBubbleAction {
  type: ActionTypes.UPDATE_BUBBLE;
  payload: { id: string; data: Partial<Bubble> };
}

interface DeleteBubbleAction {
  type: ActionTypes.DELETE_BUBBLE;
  payload: string;
}

interface SaveProjectAction {
  type: ActionTypes.SAVE_PROJECT;
}

interface LoadProjectAction {
  type: ActionTypes.LOAD_PROJECT;
  payload: ProjectState;
}

interface ClearProjectAction {
  type: ActionTypes.CLEAR_PROJECT;
}

// Union type of all actions
type ProjectAction = 
  | SetProjectNameAction
  | SetAuthorAction
  | AddPageAction
  | DeletePageAction
  | ReorderPagesAction
  | SetCurrentPageAction
  | UpdatePageAction
  | AddDrawingAction
  | AddBubbleAction
  | UpdateBubbleAction
  | DeleteBubbleAction
  | SaveProjectAction
  | LoadProjectAction
  | ClearProjectAction;

// Initial state for a new graphic novel project
const initialState: ProjectState = {
  projectName: 'My Comic Story',
  author: 'Young Creator',
  pages: [
    {
      id: '1',
      drawings: [], // Array of drawn lines
      bubbles: [],  // Array of speech/thought bubbles
      characters: [], // Placed character objects
      props: [],    // Placed prop objects
      background: null // Background image or color
    }
  ],
  currentPageIndex: 0,
  lastSaved: null
};

// Reducer function for state updates
const projectReducer = (state: ProjectState, action: ProjectAction): ProjectState => {
  switch (action.type) {
    case ActionTypes.SET_PROJECT_NAME:
      return {
        ...state,
        projectName: action.payload
      };
      
    case ActionTypes.SET_AUTHOR:
      return {
        ...state,
        author: action.payload
      };
      
    case ActionTypes.ADD_PAGE:
      // Create a new blank page
      const newPage: Page = {
        id: Date.now().toString(),
        drawings: [],
        bubbles: [],
        characters: [],
        props: [],
        background: null
      };
      
      return {
        ...state,
        pages: [...state.pages, newPage],
        currentPageIndex: state.pages.length // Set to the new page
      };
      
    case ActionTypes.DELETE_PAGE:
      // Don't delete if it's the only page
      if (state.pages.length <= 1) return state;
      
      const filteredPages = state.pages.filter((_, index) => index !== action.payload);
      const newIndex = Math.min(state.currentPageIndex, filteredPages.length - 1);
      
      return {
        ...state,
        pages: filteredPages,
        currentPageIndex: newIndex
      };
      
    case ActionTypes.REORDER_PAGES:
      // action.payload should be { fromIndex, toIndex }
      const { fromIndex, toIndex } = action.payload;
      const reorderedPages = [...state.pages];
      const [movedPage] = reorderedPages.splice(fromIndex, 1);
      reorderedPages.splice(toIndex, 0, movedPage);
      
      // Adjust current page index if needed
      let updatedPageIndex = state.currentPageIndex;
      if (state.currentPageIndex === fromIndex) {
        updatedPageIndex = toIndex;
      } else if (
        fromIndex < state.currentPageIndex && 
        toIndex >= state.currentPageIndex
      ) {
        updatedPageIndex--;
      } else if (
        fromIndex > state.currentPageIndex && 
        toIndex <= state.currentPageIndex
      ) {
        updatedPageIndex++;
      }
      
      return {
        ...state,
        pages: reorderedPages,
        currentPageIndex: updatedPageIndex
      };
      
    case ActionTypes.SET_CURRENT_PAGE:
      return {
        ...state,
        currentPageIndex: action.payload
      };
      
    case ActionTypes.UPDATE_PAGE:
      // action.payload should be { index, data }
      const updatedPages = [...state.pages];
      updatedPages[action.payload.index] = {
        ...updatedPages[action.payload.index],
        ...action.payload.data
      };
      
      return {
        ...state,
        pages: updatedPages
      };
      
    case ActionTypes.ADD_DRAWING:
      // action.payload is the drawing data
      const pagesWithNewDrawing = [...state.pages];
      const currentPage = pagesWithNewDrawing[state.currentPageIndex];
      
      pagesWithNewDrawing[state.currentPageIndex] = {
        ...currentPage,
        drawings: [...currentPage.drawings, action.payload]
      };
      
      return {
        ...state,
        pages: pagesWithNewDrawing
      };
      
    case ActionTypes.ADD_BUBBLE:
      // action.payload is the bubble data
      const pagesWithNewBubble = [...state.pages];
      const currentPageForBubble = pagesWithNewBubble[state.currentPageIndex];
      
      pagesWithNewBubble[state.currentPageIndex] = {
        ...currentPageForBubble,
        bubbles: [...currentPageForBubble.bubbles, action.payload]
      };
      
      return {
        ...state,
        pages: pagesWithNewBubble
      };
      
    case ActionTypes.UPDATE_BUBBLE:
      // action.payload should be { id, data }
      const pagesWithUpdatedBubble = [...state.pages];
      const pageWithBubble = pagesWithUpdatedBubble[state.currentPageIndex];
      
      pagesWithUpdatedBubble[state.currentPageIndex] = {
        ...pageWithBubble,
        bubbles: pageWithBubble.bubbles.map(bubble => 
          bubble.id === action.payload.id 
            ? { ...bubble, ...action.payload.data } 
            : bubble
        )
      };
      
      return {
        ...state,
        pages: pagesWithUpdatedBubble
      };
      
    case ActionTypes.DELETE_BUBBLE:
      // action.payload is the bubble id
      const pagesAfterBubbleDeletion = [...state.pages];
      const pageForBubbleDeletion = pagesAfterBubbleDeletion[state.currentPageIndex];
      
      pagesAfterBubbleDeletion[state.currentPageIndex] = {
        ...pageForBubbleDeletion,
        bubbles: pageForBubbleDeletion.bubbles.filter(
          bubble => bubble.id !== action.payload
        )
      };
      
      return {
        ...state,
        pages: pagesAfterBubbleDeletion
      };
      
    case ActionTypes.SAVE_PROJECT:
      return {
        ...state,
        lastSaved: new Date()
      };
      
    case ActionTypes.LOAD_PROJECT:
      // action.payload is the entire project data
      return {
        ...action.payload,
        lastSaved: new Date()
      };
      
    case ActionTypes.CLEAR_PROJECT:
      return initialState;
      
    // Character and prop actions would be implemented similarly to bubbles
    // ...
      
    default:
      return state;
  }
};

// Define context type
interface ProjectContextType {
  projectName: string;
  author: string;
  pages: Page[];
  currentPageIndex: number;
  lastSaved: Date | null;
  setProjectName: (name: string) => void;
  setAuthor: (author: string) => void;
  addPage: () => void;
  deletePage: (pageIndex: number) => void;
  reorderPages: (fromIndex: number, toIndex: number) => void;
  setCurrentPage: (pageIndex: number) => void;
  updatePage: (pageIndex: number, data: Partial<Page>) => void;
  addDrawing: (drawingData: Drawing) => void;
  addBubble: (bubbleData: Bubble) => void;
  updateBubble: (bubbleId: string, data: Partial<Bubble>) => void;
  deleteBubble: (bubbleId: string) => void;
  saveProject: () => void;
  loadProject: () => void;
  clearProject: () => void;
}

// Create context with default value
const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

// Provider component props
interface ProjectProviderProps {
  children: ReactNode;
}

// Provider component
export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(projectReducer, initialState);
  
  // Helper methods to dispatch actions
  const setProjectName = (name: string): void => {
    dispatch({ type: ActionTypes.SET_PROJECT_NAME, payload: name });
  };
  
  const setAuthor = (author: string): void => {
    dispatch({ type: ActionTypes.SET_AUTHOR, payload: author });
  };
  
  const addPage = (): void => {
    dispatch({ type: ActionTypes.ADD_PAGE });
  };
  
  const deletePage = (pageIndex: number): void => {
    dispatch({ type: ActionTypes.DELETE_PAGE, payload: pageIndex });
  };
  
  const reorderPages = (fromIndex: number, toIndex: number): void => {
    dispatch({ 
      type: ActionTypes.REORDER_PAGES, 
      payload: { fromIndex, toIndex } 
    });
  };
  
  const setCurrentPage = (pageIndex: number): void => {
    dispatch({ type: ActionTypes.SET_CURRENT_PAGE, payload: pageIndex });
  };
  
  const updatePage = (pageIndex: number, data: Partial<Page>): void => {
    dispatch({ 
      type: ActionTypes.UPDATE_PAGE, 
      payload: { index: pageIndex, data } 
    });
  };
  
  const addDrawing = (drawingData: Drawing): void => {
    dispatch({ type: ActionTypes.ADD_DRAWING, payload: drawingData });
  };
  
  const addBubble = (bubbleData: Bubble): void => {
    dispatch({ type: ActionTypes.ADD_BUBBLE, payload: bubbleData });
  };
  
  const updateBubble = (bubbleId: string, data: Partial<Bubble>): void => {
    dispatch({ 
      type: ActionTypes.UPDATE_BUBBLE, 
      payload: { id: bubbleId, data } 
    });
  };
  
  const deleteBubble = (bubbleId: string): void => {
    dispatch({ type: ActionTypes.DELETE_BUBBLE, payload: bubbleId });
  };
  
  const saveProject = (): void => {
    // In a real app, this would save to localStorage or a server
    dispatch({ type: ActionTypes.SAVE_PROJECT });
    
    try {
      localStorage.setItem(
        'graphicNovelProject', 
        JSON.stringify(state)
      );
    } catch (error) {
      console.error('Failed to save project', error);
    }
  };
  
  const loadProject = (): void => {
    try {
      const savedData = localStorage.getItem('graphicNovelProject');
      if (savedData) {
        const projectData = JSON.parse(savedData) as ProjectState;
        dispatch({ type: ActionTypes.LOAD_PROJECT, payload: projectData });
      }
    } catch (error) {
      console.error('Failed to load project', error);
    }
  };
  
  const clearProject = (): void => {
    if (window.confirm('Are you sure you want to start a new project? All unsaved work will be lost.')) {
      dispatch({ type: ActionTypes.CLEAR_PROJECT });
    }
  };
  
  // Get current page data
  const currentPage = state.pages[state.currentPageIndex];
  
  // Value object with state and all helper functions
  const value: ProjectContextType = {
    // State
    projectName: state.projectName,
    author: state.author,
    pages: state.pages,
    currentPageIndex: state.currentPageIndex,
    // Do not include currentPage property in the value object
    lastSaved: state.lastSaved,
    
    // Actions
    setProjectName,
    setAuthor,
    addPage,
    deletePage,
    reorderPages,
    setCurrentPage,
    updatePage,
    addDrawing,
    addBubble,
    updateBubble,
    deleteBubble,
    saveProject,
    loadProject,
    clearProject
  };
  
  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

// Custom hook for using this context
export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};